import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  alpha,
  useTheme,
  Paper,
  Slide,
  Divider,
  Autocomplete,
  InputAdornment,
  Avatar,
  useMediaQuery
} from "@mui/material";
import { getMoviePosterUrl } from "../../utils/imageUtils";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  Add as PlusIcon,
  Edit as EditIcon,
  Delete as TrashIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  Movie as MovieIcon,
  TheaterComedy as TheaterIcon,
  Refresh as RefreshIcon,
  LocalActivity as TicketIcon,
  CurrencyRupee as RupeeIcon,
  Cancel as CancelIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import api, { moviesAPI, theatersAPI } from "../../services/api";
import showsService from "../../services/showsService";
import ModernLoader from "../../components/common/ModernLoader";
import moment from "moment";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminShows = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [shows, setShows] = useState([]);
  const [movies, setMovies] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTheater, setSelectedTheater] = useState(null);

  const [formData, setFormData] = useState({
    movie: "",
    theater: "",
    date: null,
    time: null,
    silverPrice: "150",
    goldPrice: "200",
    premiumPrice: "300",
    screenNumber: "1",
  });

  const fetchShows = useCallback(async () => {
    try {
      setLoading(true);
      const timestamp = new Date().getTime();
      const response = await api.get(`/shows?includeBookingClosed=true&admin=true&_t=${timestamp}`);
      let data = Array.isArray(response.data) ? response.data : (response.data.shows || response.data.data || []);
      setShows(data);
    } catch (error) {
      toast.error("Telemetry sync failure");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMovies = useCallback(async () => {
    try {
      const response = await moviesAPI.getAll();
      setMovies(response.data || []);
    } catch (error) {
      console.error("Failed to fetch movies");
    }
  }, []);

  const fetchTheaters = useCallback(async () => {
    try {
      const response = await theatersAPI.getAll();
      const data = response.data.theaters || response.data.data || response.data || [];
      setTheaters(Array.isArray(data) ? data.filter(t => t && t._id) : []);
    } catch (error) {
      console.error("Failed to fetch theaters");
    }
  }, []);

  useEffect(() => {
    fetchShows();
    fetchMovies();
    fetchTheaters();
    
    // Setup real-time listeners for admin dashboard
    const handleShowsUpdate = () => fetchShows();
    const handleMoviesUpdate = () => fetchMovies();
    const handleTheatersUpdate = () => fetchTheaters();
    
    window.addEventListener("shows-updated", handleShowsUpdate);
    window.addEventListener("movies-updated", handleMoviesUpdate);
    window.addEventListener("theaters-updated", handleTheatersUpdate);
    window.addEventListener("seat-layout-updated", handleTheatersUpdate);
    
    return () => {
      window.removeEventListener("shows-updated", handleShowsUpdate);
      window.removeEventListener("movies-updated", handleMoviesUpdate);
      window.removeEventListener("theaters-updated", handleTheatersUpdate);
      window.removeEventListener("seat-layout-updated", handleTheatersUpdate);
    };
  }, [fetchShows, fetchMovies, fetchTheaters]);

  const handleOpenDialog = (show = null) => {
    if (show) {
      setEditingShow(show);
      const theater = theaters.find(t => t._id === (show.theater?._id || show.theater));
      setSelectedTheater(theater);
      setFormData({
        movie: show.movie?._id || show.movie || "",
        theater: show.theater?._id || show.theater || "",
        date: show.showDate ? moment(show.showDate) : null,
        time: show.showTime ? moment(show.showTime, "HH:mm") : null,
        silverPrice: show.pricing?.silver || show.price || "150",
        goldPrice: show.pricing?.gold || Math.round((show.price || 150) * 1.3),
        premiumPrice: show.pricing?.premium || Math.round((show.price || 150) * 1.8),
        screenNumber: show.screenNumber || "1",
      });
    } else {
      setEditingShow(null);
      setFormData({
        movie: "",
        theater: "",
        date: null,
        time: null,
        silverPrice: "150",
        goldPrice: "200",
        premiumPrice: "300",
        screenNumber: "1",
      });
      setSelectedTheater(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShow(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleValueChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date || !formData.time) {
      toast.warning("Incomplete session timeline");
      return;
    }

    try {
      setSubmitting(true);
      const silverPrice = parseInt(formData.silverPrice) || 150;
      const showData = {
        movie: formData.movie,
        theater: formData.theater,
        showDate: formData.date.format("YYYY-MM-DD"),
        showTime: formData.time.format("HH:mm"),
        price: silverPrice,
        screenNumber: parseInt(formData.screenNumber) || 1,
        pricing: {
          silver: silverPrice,
          gold: parseInt(formData.goldPrice) || Math.round(silverPrice * 1.3),
          premium: parseInt(formData.premiumPrice) || Math.round(silverPrice * 1.8),
        },
      };

      if (editingShow) {
        await showsService.update(editingShow._id, showData);
        toast.success("Schedule optimized");
      } else {
        await showsService.create(showData);
        toast.success("New theatrical stream authorized");
      }
      handleCloseDialog();
      fetchShows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operational deadlock");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, movieName) => {
    if (window.confirm(`Decommission stream for "${movieName}"?`)) {
      try {
        await showsService.delete(id);
        toast.success("Session decommissioned");
        fetchShows();
      } catch (error) {
        toast.error("Purge failure");
      }
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "movie.title",
      header: "Project Origin",
      Cell: ({ row, cell }) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), color: "primary.main", borderRadius: "12px" }}>
            <MovieIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 850 }}>{cell.getValue() || "Legacy"}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>AUDITORIUM {row.original.screenNumber}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      accessorKey: "theater.name",
      header: "Theatrical Venue",
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontWeight: 650 }}>{cell.getValue() || "N/A"}</Typography>
      ),
    },
    {
      accessorKey: "showDate",
      header: "Operational Date",
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontWeight: 800 }}>{moment(cell.getValue()).format("MMM DD, YYYY")}</Typography>
      ),
    },
    {
      accessorKey: "showTime",
      header: "Sync Time",
      Cell: ({ cell }) => (
        <Chip
          label={moment(`2000-01-01T${cell.getValue()}`).format("hh:mm A")}
          size="small"
          sx={{ fontWeight: 950, fontSize: "0.65rem", bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" }}
        />
      ),
    },
    {
      accessorKey: "status",
      header: "Performance Status",
      Cell: ({ row, cell }) => {
        const val = cell.getValue() || (row.original.isPast ? "Show Started" : "Upcoming");
        const isEnded = val === "Show Ended" || row.original.isEnded;
        const isStarted = val === "Show Started" || (row.original.isPast && !isEnded);
        const isClosed = val === "Booking Closed" || row.original.bookingClosed;

        let color = "success";
        let label = val;

        if (isEnded) { color = "error"; label = "Show Ended"; }
        else if (isStarted) { color = "warning"; label = "Performance Started"; }
        else if (isClosed) { color = "secondary"; label = "Booking Closed"; }

        return (
          <Chip
            label={label.toUpperCase()}
            size="small"
            color={color}
            sx={{ fontWeight: 900, fontSize: "0.6rem", borderRadius: "6px" }}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Command",
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleOpenDialog(row.original)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.original._id, row.original.movie?.title)} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: "error.main" }}><TrashIcon sx={{ fontSize: 16 }} /></IconButton>
        </Stack>
      ),
    },
  ], [theme.palette]);

  const table = useMaterialReactTable({
    columns,
    data: shows,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "30px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.primary.main, 0.05), py: 2.5, fontWeight: 900, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.1em", color: theme.palette.text.secondary } }
  });

  if (loading && shows.length === 0) return <ModernLoader text="ESTABLISHING NETWORK STREAMS" />;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ width: "100%", pb: 4 }}>

          {/* Core Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            mb={{ xs: 3, md: 5 }}
            gap={{ xs: 2.5, md: 3 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
                <Box sx={{ p: 1, borderRadius: "12px", bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main, display: "flex", flexShrink: 0 }}>
                  <CalendarIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
                Session Scheduling
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TicketIcon sx={{ fontSize: 16, color: "secondary.main" }} /> CINEMATIC AVAILABILITY & PERFORMANCE SYNC PROTOCOLS
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchShows}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "14px",
                  px: 3,
                  fontWeight: 900,
                  height: "54px",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  bgcolor: theme.palette.background.paper
                }}
              >
                Sync
              </Button>
              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={() => handleOpenDialog()}
                fullWidth={isMobile}
                sx={{
                  py: 1.8,
                  px: 4.5,
                  borderRadius: "16px",
                  fontWeight: 900,
                  background: theme.palette.primary.main,
                  boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                  textTransform: "none",
                  "&:hover": { background: theme.palette.primary.dark, transform: "translateY(-2px)" },
                  transition: "all 0.2s"
                }}
              >
                New Session
              </Button>
            </Stack>
          </Stack>

          <MaterialReactTable table={table} />

          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="lg"
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: isMobile ? 0 : "28px",
                backgroundImage: "none",
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.palette.mode === 'dark' ? "0 25px 80px -12px rgba(0, 0, 0, 0.7)" : "0 25px 50px -12px rgba(19, 19, 22, 0.25)",
                maxHeight: "92vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              }
            }}
          >
            <form onSubmit={handleSubmit} style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box sx={{
                p: { xs: 3, md: 4 },
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.background.default, 0.5),
                flexShrink: 0
              }}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Box sx={{
                    p: 1.5,
                    borderRadius: "16px",
                    bgcolor: "secondary.main",
                    color: theme.palette.secondary.contrastText,
                    display: "flex",
                    boxShadow: "0 8px 16px -4px " + alpha(theme.palette.secondary.main, 0.5)
                  }}>
                    <CalendarIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.text.primary, letterSpacing: "-0.02em" }}>
                      {editingShow ? "Re-Schedule Performance" : "Authorize New Session"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {editingShow ? `Session ID: ${editingShow._id}` : "Awaiting Deployment"}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton onClick={handleCloseDialog} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03), "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) } }}>
                  <CancelIcon />
                </IconButton>
              </Box>

              <DialogContent sx={{ p: { xs: 3, md: 5 }, bgcolor: theme.palette.background.paper, flexGrow: 1, overflowY: "auto" }}>
                <Grid container spacing={5}>
                  {/* Performance Target Selection */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, color: "secondary.main", letterSpacing: "0.15em" }}>OPERATIONAL TARGETS</Typography>
                    <Grid container spacing={3} mt={0.5}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>PRODUCTION ASSET</Typography>
                        <Autocomplete
                          options={movies}
                          getOptionLabel={(o) => o.title || ""}
                          value={movies.find(m => m._id === formData.movie) || null}
                          onChange={(e, v) => handleValueChange("movie", v?._id || "")}
                          renderInput={(p) => <TextField {...p} placeholder="Enroll movie" variant="outlined" required sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4), fontWeight: 900 } }} />}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar src={getMoviePosterUrl(option.poster, option.title)} variant="rounded" sx={{ width: 32, height: 44, borderRadius: "6px" }} />
                                <Typography sx={{ fontWeight: 800 }}>{option.title}</Typography>
                              </Stack>
                            </Box>
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>THEATRICAL HUB</Typography>
                        <Autocomplete
                          options={theaters}
                          getOptionLabel={(o) => o.name || ""}
                          value={theaters.find(t => t._id === formData.theater) || null}
                          onChange={(e, v) => { setSelectedTheater(v); handleValueChange("theater", v?._id || ""); handleValueChange("screenNumber", "1"); }}
                          renderInput={(p) => <TextField {...p} placeholder="Select hub" variant="outlined" required sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4), fontWeight: 800 } }} />}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ borderStyle: "dashed" }} />
                  </Grid>

                  {/* Temporal Protocols */}
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, color: "secondary.main", letterSpacing: "0.15em" }}>TEMPORAL PROTOCOLS</Typography>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), mt: 1.5 }}>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mb: 1, display: "block" }}>DEPLOYMENT DATE</Typography>
                          <DatePicker value={formData.date} onChange={(v) => handleValueChange("date", v)} slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3) } } } }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mb: 1, display: "block" }}>SYNC START TIME</Typography>
                          <TimePicker value={formData.time} onChange={(v) => handleValueChange("time", v)} slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3) } } } }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mb: 1, display: "block" }}>STATION NODE (SCREEN)</Typography>
                          <TextField fullWidth select name="screenNumber" value={formData.screenNumber} onChange={handleInputChange} variant="outlined" InputProps={{ sx: { borderRadius: "14px", bgcolor: alpha(theme.palette.background.default, 0.3), fontWeight: 900 } }}>
                            {selectedTheater?.screens?.map(s => <MenuItem key={s.screenNumber} value={s.screenNumber.toString()} sx={{ fontWeight: 800 }}>STATION {s.name || s.screenNumber}</MenuItem>) || <MenuItem value="1">Primary Node</MenuItem>}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, color: "secondary.main", letterSpacing: "0.15em" }}>STATUS INTELLIGENCE</Typography>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: "1.5px solid", borderColor: "divider", bgcolor: alpha(theme.palette.secondary.main, 0.05), mt: 1.5, height: "calc(100% - 30px)", display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
                      <TicketIcon sx={{ fontSize: 40, color: "secondary.main", mb: 2, mx: "auto" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 950, color: "secondary.main", letterSpacing: "0.05em" }}>PROTOCOL READY</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, mt: 1 }}>ALL SYSTEMS SYNCED</Typography>
                    </Paper>
                  </Grid>

                  {/* Revenue Allocations */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="overline" sx={{ fontWeight: 900, color: "secondary.main", letterSpacing: "0.15em" }}>REVENUE ALLOCATION (₹)</Typography>
                    <Grid container spacing={3} mt={0.5}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "secondary.main", mb: 1.5, display: "block", letterSpacing: "0.02em" }}>SILVER TIER</Typography>
                          <TextField fullWidth name="silverPrice" type="number" value={formData.silverPrice} onChange={handleInputChange} InputProps={{ startAdornment: <RupeeIcon sx={{ mr: 1, fontSize: 18, color: "secondary.main" }} />, sx: { borderRadius: "14px", fontWeight: 950, bgcolor: alpha(theme.palette.background.default, 0.2), fontSize: "1.1rem" } }} />
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "primary.main", mb: 1.5, display: "block", letterSpacing: "0.02em" }}>GOLD DEPLOYMENT</Typography>
                          <TextField fullWidth name="goldPrice" type="number" value={formData.goldPrice} onChange={handleInputChange} InputProps={{ startAdornment: <RupeeIcon sx={{ mr: 1, fontSize: 18, color: "primary.main" }} />, sx: { borderRadius: "14px", fontWeight: 950, bgcolor: alpha(theme.palette.background.default, 0.2), fontSize: "1.1rem" } }} />
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "warning.main", mb: 1.5, display: "block", letterSpacing: "0.02em" }}>PREMIUM ACCESS</Typography>
                          <TextField fullWidth name="premiumPrice" type="number" value={formData.premiumPrice} onChange={handleInputChange} InputProps={{ startAdornment: <RupeeIcon sx={{ mr: 1, fontSize: 18, color: "warning.main" }} />, sx: { borderRadius: "14px", fontWeight: 950, bgcolor: alpha(theme.palette.background.default, 0.2), fontSize: "1.1rem" } }} />
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>

              <Box sx={{
                p: { xs: 3, md: 4 },
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                flexShrink: 0
              }}>
                <Button onClick={handleCloseDialog} sx={{ fontWeight: 800, color: "text.secondary", px: 4, borderRadius: "14px", textTransform: "none" }}>Dismiss</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    borderRadius: "16px",
                    px: 6,
                    py: 1.8,
                    fontWeight: 900,
                    textTransform: "none",
                    bgcolor: "secondary.main",
                    boxShadow: "0 12px 24px -6px " + alpha(theme.palette.secondary.main, 0.4),
                    "&:hover": { transform: "translateY(-2px)", bgcolor: "secondary.dark", boxShadow: "0 16px 32px -8px " + alpha(theme.palette.secondary.main, 0.5) }
                  }}
                >
                  {submitting ? "Processing..." : (editingShow ? "Safeguard Schedule" : "Authorize Session")}
                </Button>
              </Box>
            </form>
          </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminShows;
